import { memo } from "react";
import { NavLink } from "react-router-dom";
import { PanelLeftClose, PanelLeft } from "lucide-react";
import type { NavItem } from "@/types";
import { PRIMARY_NAV, SECONDARY_NAV } from "@/router/navigation";
import { useAppStore } from "@/stores";
import { cn } from "@/utils";
import { Kbd } from "@/components/ui";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

/**
 * Sidebar
 *
 * The primary navigation rail. Collapses to an icon-only column, remembering
 * the choice via the app store. Active route is derived from the router, so
 * the highlight stays correct regardless of how navigation happened.
 */
function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "group relative flex h-8 items-center gap-2.5 rounded-md px-2.5 text-base",
          "transition-colors duration-fast ease-standard no-drag",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus",
          isActive
            ? "bg-bg-hover text-text-primary"
            : "text-text-secondary hover:bg-bg-hover hover:text-text-primary",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Active marker — a precise 2px rule, never a colour block. */}
          <span
            className={cn(
              "absolute left-0 h-4 w-0.5 rounded-full bg-accent transition-opacity duration-fast",
              isActive ? "opacity-100" : "opacity-0",
            )}
            aria-hidden
          />
          <Icon size={16} strokeWidth={1.75} className="shrink-0" aria-hidden />
          {!collapsed && (
            <>
              <span className="flex-1 truncate">{item.label}</span>
              {item.shortcut && (
                <Kbd className="opacity-0 transition-opacity group-hover:opacity-100">
                  {item.shortcut}
                </Kbd>
              )}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

export const Sidebar = memo(function Sidebar() {
  const collapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggle = useAppStore((s) => s.toggleSidebar);

  return (
    <nav
      aria-label="Primary"
      className={cn(
        "flex h-full shrink-0 flex-col border-r border-white/5 bg-bg-elevated/70 backdrop-blur-xl backdrop-saturate-150",
        "transition-[width] duration-base ease-standard",
        collapsed ? "w-14" : "w-56",
      )}
    >
      {/* Brand / drag handle */}
      <div className="drag-region flex h-11 items-center gap-2.5 px-3.5">
        <div
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-border-strong"
          aria-hidden
        >
          <span className="font-mono text-2xs font-semibold text-text-primary">
            S
          </span>
        </div>
        {!collapsed && (
          <span className="truncate text-base font-semibold tracking-tight text-text-primary">
            Seufic AI
          </span>
        )}
      </div>

      <div className="px-2 pb-1">
        <WorkspaceSwitcher collapsed={collapsed} />
      </div>

      <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-2">
        {PRIMARY_NAV.map((item) => (
          <NavRow key={item.id} item={item} collapsed={collapsed} />
        ))}
      </div>

      <div className="flex flex-col gap-0.5 border-t border-border px-2 py-2">
        {SECONDARY_NAV.map((item) => (
          <NavRow key={item.id} item={item} collapsed={collapsed} />
        ))}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="no-drag mt-0.5 flex h-8 items-center gap-2.5 rounded-md px-2.5 text-base text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus"
        >
          {collapsed ? (
            <PanelLeft size={16} strokeWidth={1.75} aria-hidden />
          ) : (
            <>
              <PanelLeftClose size={16} strokeWidth={1.75} aria-hidden />
              <span className="flex-1 text-left">Collapse</span>
            </>
          )}
        </button>
      </div>
    </nav>
  );
});
